/**
 * The `firstshiftbooked` admin endpoint aliases its ids — the talent is
 * `HygienistId` and the shift is `JobId` — and returns no plain `id` column.
 *
 * The Talent view's row-click handler and every membership action read
 * `hygienist.id`. Without normalising, clicking a row on the First Shift Booked tab
 * sent `undefined` as the talent id, which MySQL coerced to `0`. Because `0` is the
 * sentinel for "nobody is booked for this shift" rather than an empty match, the
 * query matched 194,042 of 382,800 Job rows and took production down on 2026-08-04.
 *
 * Normalising here rather than in the shared SQL: `FirstShiftBooked.vue` consumes the
 * same endpoint and deliberately keys off `JobId` / `HygienistId`, so changing the
 * query would risk that view.
 */
export interface FirstShiftBookedRow {
  HygienistId?: number | null;
  JobId?: number | null;
  [key: string]: any;
}

/**
 * Copies `HygienistId` onto `id` so the row satisfies what the Talent view expects.
 * `JobId` and `HygienistId` are preserved, since other views rely on them.
 *
 * A row without a usable `HygienistId` is left without an `id` rather than being
 * given a bogus one — the API now rejects a non-positive-integer id with 400, so a
 * missing id degrades to an empty detail panel instead of a full-table scan.
 */
export function normalizeFirstShiftBookedRows<T extends FirstShiftBookedRow>(
  records: T[] | null | undefined
): T[] {
  if (!Array.isArray(records)) {
    return [];
  }

  return records.map(record => {
    const hygienistId = Number(record?.HygienistId);

    if (!Number.isInteger(hygienistId) || hygienistId <= 0) {
      return record;
    }

    return { ...record, id: hygienistId };
  });
}

export default normalizeFirstShiftBookedRows;
